import { FleetViewTireWearTranslations } from '../TireInfo/tireWearTranslations';

/**
 * Translations for the FleetView table columns
 */
export type FleetViewColumnsTranslations = {
  car: string;
  driver: string;
  customer: string;
  wheelSetAtTireHotel: string;
  none: string;
  lastBooking: string;
  upcomingBooking: string;
  summerTires: string;
  winterTires: string;
  noMeasurements: string;
  mustBeReplaced: string;
  shouldBeReplaced: string;
  goodCondition: string;
  allSeasonTires: string;
  unknownSeason: string;
  cancelled: string;
  ownerSince: (date: string) => string;
  currentOwners: (count: number) => string;
  leased: string;
  fleetSchedule: string;
};

export const defaultFleetViewColumnsTranslations: FleetViewColumnsTranslations = {
  car: 'Car',
  driver: 'Driver',
  customer: 'Customer',
  wheelSetAtTireHotel: 'Wheel set at tire hotel',
  none: 'None',
  lastBooking: 'Last booking',
  upcomingBooking: 'Upcoming booking',
  summerTires: 'Summer tires',
  winterTires: 'Winter tires',
  noMeasurements: 'No measurements',
  mustBeReplaced: 'Must be replaced',
  shouldBeReplaced: 'Should be replaced',
  goodCondition: 'Good condition',
  allSeasonTires: 'All Season',
  unknownSeason: 'Unknown',
  cancelled: 'Cancelled',
  ownerSince: (date) => `Owner since ${date}`,
  currentOwners: (count) => `${count} current owners`,
  leased: 'Leased',
  fleetSchedule: 'Fleet schedule'
};

/**
 * Translations for the FleetView table filters
 */
export type FleetViewFilterTranslations = {
  hasUpcomingBooking: string;
  hasUpcomingBookingDescription: string;
  upcomingBookingDateRange: string;
  upcomingBookingDateRangeDescription: string;
  lastBookingDateRange: string;
  lastBookingDateRangeDescription: string;
  wheelSetInStorage: string;
  wheelSetInStorageDescription: string;
  winter: string;
  summer: string;
  none: string;
  carMakes: string;
  carMakesDescription: string;
  carModels: string;
  carModelsDescription: string;
  summerTireCondition: string;
  winterTireCondition: string;
  mustBeReplaced: string;
  shouldBeReplaced: string;
  goodCondition: string;
  noMeasurements: string;
  tireWearInfo?: FleetViewTireWearTranslations;
};

export const defaultFleetViewFilterTranslations: FleetViewFilterTranslations = {
  hasUpcomingBooking: 'Upcoming booking',
  hasUpcomingBookingDescription: 'Specifies if the car has a booking that is not completed or canceled',
  upcomingBookingDateRange: 'Upcoming booking date range',
  upcomingBookingDateRangeDescription: "Filter by the date of the car's next upcoming booking",
  lastBookingDateRange: 'Last booking date range',
  lastBookingDateRangeDescription: "Filter by the date of the car's last completed booking",
  wheelSetInStorage: 'Wheel set in storage',
  wheelSetInStorageDescription: 'Filter by which tire season is currently stored in the tire hotel',
  // eslint-disable-next-line noddi/no-emoji-in-ui -- season icon in filter option label; option labels are plain strings with no icon slot
  winter: '❄️ Winter',
  // eslint-disable-next-line noddi/no-emoji-in-ui -- season icon in filter option label; option labels are plain strings with no icon slot
  summer: '☀️ Summer',
  // eslint-disable-next-line noddi/no-emoji-in-ui -- marker in filter option label; option labels are plain strings with no icon slot
  none: '🚫 None',
  carMakes: 'Car makes',
  carMakesDescription: 'Filter by specific car makes',
  carModels: 'Car models',
  carModelsDescription: 'Filter by specific car models',
  summerTireCondition: 'Summer tire condition',
  winterTireCondition: 'Winter tire condition',
  mustBeReplaced: 'Must be replaced',
  shouldBeReplaced: 'Should be replaced',
  goodCondition: 'Good condition',
  noMeasurements: 'No measurements'
};

/**
 * Translations for the FleetView summary charts
 */
export type FleetViewSummaryChartsTranslations = {
  storedWheelSetsBySeason: string;
  notOnTireHotel: string;
  summerTires: string;
  winterTires: string;
  noCarsInFleet: string;
  carsHaveWheelsStored: (stored: number, total: number) => string;
  summerTireCondition: string;
  winterTireCondition: string;
  shouldBeReplaced: string;
  mustBeReplaced: string;
  goodCondition: string;
  noMeasurements: string;
  noMeasurementsAvailable: string;
  carsWithMeasurements: (measured: number, total: number) => string;
  upcomingBookings: string;
  hasUpcomingBooking: string;
  noUpcomingBooking: string;
  carsWithUpcomingBookings: (withBooking: number, total: number) => string;
  tireWearInfo?: FleetViewTireWearTranslations;
};

export const defaultFleetViewSummaryChartsTranslations: FleetViewSummaryChartsTranslations = {
  storedWheelSetsBySeason: 'Wheel set in storage',
  notOnTireHotel: 'None',
  summerTires: 'Summer tires',
  winterTires: 'Winter tires',
  noCarsInFleet: 'No cars in fleet',
  carsHaveWheelsStored: (stored: number, total: number) => `${stored} of ${total} cars have wheels stored`,
  summerTireCondition: 'Summer tire condition',
  winterTireCondition: 'Winter tire condition',
  shouldBeReplaced: 'Should be replaced',
  goodCondition: 'Good condition',
  mustBeReplaced: 'Must be replaced',
  noMeasurements: 'No measurements',
  noMeasurementsAvailable: 'No measurements available',
  carsWithMeasurements: (measured: number, total: number) => `${measured} of ${total} cars have tire measurements`,
  upcomingBookings: 'Upcoming bookings',
  hasUpcomingBooking: 'Has booking',
  noUpcomingBooking: 'No booking',
  carsWithUpcomingBookings: (withBooking: number, total: number) =>
    `${withBooking} of ${total} cars have upcoming bookings`
};

/**
 * Subset types for individual chart components
 */
export type StorageBySeasonChartTranslations = Pick<
  FleetViewSummaryChartsTranslations,
  | 'storedWheelSetsBySeason'
  | 'summerTires'
  | 'winterTires'
  | 'notOnTireHotel'
  | 'noCarsInFleet'
  | 'carsHaveWheelsStored'
>;

export type TireWearChartTranslations = Pick<
  FleetViewSummaryChartsTranslations,
  | 'summerTireCondition'
  | 'winterTireCondition'
  | 'shouldBeReplaced'
  | 'mustBeReplaced'
  | 'goodCondition'
  | 'noMeasurements'
  | 'noMeasurementsAvailable'
  | 'carsWithMeasurements'
  | 'tireWearInfo'
>;

export type UpcomingBookingsChartTranslations = Pick<
  FleetViewSummaryChartsTranslations,
  'upcomingBookings' | 'hasUpcomingBooking' | 'noUpcomingBooking' | 'noCarsInFleet' | 'carsWithUpcomingBookings'
>;
